import { useState, useEffect } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';
import _ from 'lodash';

import { MDB_TYPES } from '@meddbriefer/scenario-data/constants';

import { useScenarioEditor } from '../ScenarioEditor/ScenarioEditorContext';

import ProblemsLayout from './ProblemsLayout';
import { ModelAdmin, InlineModelAdmin, AddModel, AssociateModel  } from '../admins';

import { Checkbox, DropDown, Text, TextArea, TextInput } from '../widgets';
import { genSolutionRec } from '../ScenarioTypeEditor/InterventionsEditor/types';
import { getProblemsBC, getProblemBC } from '../ScenarioTypeEditor/urls';
import {appendObjToArray, updateObjInArray, removeObjWithIdFromArray } from '../ScenarioTypeEditor/utils';

const VITALS_OPTIONS = ["BP", "P", "R", "Skin", "Sp02", "ETCO2", "GCS", "Glucose", "Pain", "Temp"].map(e => ({
    id: e,
    label: e
}))

const VARIANT_OPTIONS = ["initial", "good", "bad"].map(e => ({id: e, label: e}))

const assessRE = /^problem-\d+-assessment-(\d+)$/
const noteRE = /^problem-\d+-note-(\d+)$/
const solutionRE = /^problem-\d+-solution-(\d+)$/

const Problem2 = () => {
    const params = useParams()
    const {probID} = params
    const {url} = useRouteMatch()
    const {scenario, scenarioType, problems, solutions, actions, uniqueIDs } = useScenarioEditor()
    const [problem, setProblem] = useState(null)
    const [mySolutions, setMySolutions] = useState(null)
    const [solutionsUpdated, setSolutionsUpdated] = useState(false)
    const [problemUpdated, setProblemUpdated] = useState(false)

    const generateID = (records, re, fldName, recordsAreObjects=true) => {
        let nextNum = 1
        let max
        if (recordsAreObjects) {
            max = _.max(records.map(e => parseInt(e.id.match(re)[1], 10)))
        } else {
            max = _.max(records.map(e => parseInt(e.match(re)[1], 10)))
        }
        if (!!max) {
            nextNum = max + 1
        }
        const newID = `${probID}-${fldName}-${nextNum}`
        return newID
    }

    const genAssessmentID = () => generateID(problem.assessments, assessRE,   "assessment")
    const genNoteID       = () => generateID(problem.notes,       noteRE,     "note")
    const genSolutionID   = () => generateID(problem.solutions,   solutionRE, "solution", false)

    const updateProblemFld = (id, fldName, fldVal) => {
        setProblem(prevState => ({...prevState, [fldName]: fldVal}))
        setProblemUpdated(true)
    }

    const addAssessment = (_unused1, _unused2, checkListItemID) => {
        const newRec = {id: genAssessmentID(), assessmentID: checkListItemID, feedback: ""}
        updateProblemFld(probID, "assessments", appendObjToArray(problem.assessments, newRec))
    }

    const updateAssessmentFld = (assessID, fldName, fldVal) => {
        const rec = {...problem.assessments.find(e => e.id === assessID), [fldName]: fldVal}
        updateProblemFld(probID, "assessments", updateObjInArray(problem.assessments, rec))
    }

    const removeAssessment = (item) => updateProblemFld(probID, "assessments", removeObjWithIdFromArray(problem.assessments, item.id))


    const addVital = (_unused1, _unused2, vitalSign) => {
        const newRec = {id: vitalSign, variant: ""}
        updateProblemFld(probID, "vitals", appendObjToArray(problem.vitals, newRec))
    }

    const updateVitalFld = (vitalID, fldName, fldVal) => {
        const rec = {...problem.vitals.find(e => e.id === vitalID), [fldName]: fldVal}
        updateProblemFld(probID, "vitals", updateObjInArray(problem.vitals, rec))
    }

    const removeVital = (item) => updateProblemFld(probID, "vitals", removeObjWithIdFromArray(problem.vitals, item.id))

    const addNote = (entityType, id, label) => {
        const newRec = {id, label}
        updateProblemFld(probID, "notes", appendObjToArray(problem.notes, newRec))
    }

    const updateNoteFld = (noteID, fldName, fldVal) => {
        const rec = {...problem.notes.find(e => e.id === noteID), [fldName]: fldVal}
        updateProblemFld(probID, "notes", updateObjInArray(problem.notes, rec))
    }

    const removeNote = (item) => updateProblemFld(probID, "notes", removeObjWithIdFromArray(problem.notes, item.id))

    const addSolution = (entityType, id, label) => {
        solutions.add(genSolutionRec(id, label, probID))
        updateProblemFld(probID, "solutions", problem.solutions.concat(id))
        setSolutionsUpdated(true)
    }

    const updateSolution = (solID, fldName, fldVal) => {
        const found = scenario.solutions.find(e => e.id === solID)
        if (!!found) {
            solutions.update({...found, [fldName]: fldVal})
            setSolutionsUpdated(true)
        } else {
            console.log(`solution ${solID} not found`)
        }
    }

    const deleteSolutionHandler = (item) => {
        const sol = scenario.solutions.find(e => e.id === item.id)
        let msg = `Are you sure you want to delete the solution "${sol.label}"?`
        const acts = scenario.actions.filter(e => e.solutionID === item.id)
        if (acts.length > 0) {
            msg += ` and all associated actions?\n`
            msg += "Actions:\n==============\n"
            acts.forEach(act => msg += `${act.interventionID}\n`)
        }
        if (window.confirm(msg)) {
            removeSolution(item)
        }
    }

    const removeSolution = (item) => {
        // console.log("will remove solution", item)
        scenario.actions.filter(e => e.solutionID === item.id)
                        .forEach(act => actions.remove(act.id))
        solutions.remove(item.id)
        updateProblemFld(probID, "solutions", problem.solutions.filter(id => id !== item.id))
        setSolutionsUpdated(true)
    }

    const assessmentLabelGetter = (entity, fld) => {
        const found = scenarioType.getChecklistItem(entity.assessmentID)
        return (!!found) ? found.abbrLabel : null
    }

    useEffect(
        () => {
            const prob = scenario.problems.find(e => e.id === probID)
            if (!prob) {
                console.log(`problem: ${probID} not found`)
            } else {
                prob.assessments.forEach(assess => {
                    if (undefined === assess.feedback) {
                        assess.feedback = ""
                    }
                })
                setProblem(prob)
                // set solutions to updated on initial load
                setSolutionsUpdated(true)
            }
        }, [probID, scenario.problems]
    )

    useEffect(
        () => {
            if (solutionsUpdated) {
                const matches = scenario.solutions.filter(e => problem.solutions.includes(e.id))
                const tmp = []
                problem.solutions.forEach(id => {
                    const found = matches.find(e => e.id === id)
                    if (!!found) {
                        tmp.push(found)
                    } else {
                        console.log(`solution ${id} not found in matches. race condition??`)
                    }
                })
                setMySolutions(tmp)
                setSolutionsUpdated(false)
            }
        }, [solutionsUpdated, scenario, problem]
    )
    useEffect(
        () => {
            if (problemUpdated) {
                // persist state changes behond this form
                problems.update(problem)
                setProblemUpdated(false)
            }
        }, [problemUpdated, problems, problem]
    )
    if (!mySolutions) {
        return (<div>Loading...</div>)
    }
    return (
        <ProblemsLayout
            breadcrumbs={[
                getProblemsBC(params),
                getProblemBC(params, problem.label)
            ]}
        >
        <ModelAdmin
            heading="Problem"
            record={problem}
            fields={[
                { name: "id", label: "ID", widget: Text},
                { name: "label", label: "Label", widget: TextInput, changeHandler: updateProblemFld},
                { name: "lifeThreat", label: "Life Threat", widget: Checkbox, changeHandler: updateProblemFld}
            ]}
        />
        <InlineModelAdmin
            heading="Assessments"
            records={problem.assessments}
            fields={[
                { name: "assessmentID", label: "Assessment ID", colWidthPct: 20, widget: Text, textSize: "smaller"},
                { name: "label", label: "Assessment Label", colWidthPct: 40, widget: Text, valueGetter: assessmentLabelGetter},
                { name: "feedback", label: "Feedback", colWidthPct: 40, widget: TextInput, size: 60, changeHandler: updateAssessmentFld}
            ]}
            editLink={false}
            onDelete={removeAssessment}
        >
            <AssociateModel
                model={{ type: MDB_TYPES.ASSESS, name: "Assessment", labelsUnique: false }}
                search={true}
                choices={scenarioType.checkListMetaData.filter(e => e.type === MDB_TYPES.ASSESS)}
                addHandler={addAssessment}
            />
        </InlineModelAdmin>
        <div style={{display: "flex", flexDirection: "row"}}>
            <div style={{flex: "1 1 auto"}} >
                <InlineModelAdmin
                    heading="Vitals"
                    records={problem.vitals}
                    fields={[
                        { name: "id", label: "ID", colWidthPct: 40, widget: Text, textSize: "small"},
                        { name: "variant", label: "Variant", colWidthPct: 60, widget: DropDown, options: VARIANT_OPTIONS, changeHandler: updateVitalFld}
                    ]}
                    allowChangeOrder={false}
                    editLink={false}
                    onDelete={removeVital}
                >
                    <AssociateModel
                        model={{ type: "vitals", name: "Vital sign", labelsUnique: false }}
                        choices={VITALS_OPTIONS}
                        addHandler={addVital}
                        uniqueIDs={[]}
                    />
                </InlineModelAdmin>
            </div>
            <div style={{flex: "1 1 auto"}} >
                <InlineModelAdmin
                    heading="Notes"
                    records={problem.notes}
                    fields={[
                        // { name: "id", label: "ID", colWidthPct: 25, widget: Text, textSize: "small"},
                        { name: "label", label: "Label", colWidthPct: 100, widget: TextArea, cols: 100, rows: 2, changeHandler: updateNoteFld}
                    ]}
                    allowChangeOrder={false}
                    editLink={false}
                    onDelete={removeNote}
                >
                    <AddModel
                        model={{type: "note", name: "Note", labelsUnique: false}}
                        addHandler={addNote}
                        uniqueIDs={uniqueIDs}
                        uniqueLabels={[]}
                        idGenerator={genNoteID}
                    />
                </InlineModelAdmin>
            </div>
        </div>
        <InlineModelAdmin
            heading="Solutions"
            records={mySolutions}
            fields={[
                // { name: "id", label: "ID", colWidthPct: 10, widget: Text, textSize: "xx-small"},
                { name: "label", label: "Label", colWidthPct: 25, widget: TextInput, changeHandler: updateSolution},
                { name: "protocols", label: "Protocols", colWidthPct: 25, widget: TextInput, changeHandler: updateSolution},
                { name: "minimalWhy", label: "Minimal Why", colWidthPct: 50, widget: TextArea, cols: 70, rows: 2, changeHandler: updateSolution }
            ]}
            allowChangeOrder={true}
            editLink={true}
            editBaseURL={`${url}/solutions`}
            onDelete={deleteSolutionHandler}
        >
            <AddModel
                model={{type: "solution", name: "Solution", labelsUnique: false}}
                addHandler={addSolution}
                uniqueIDs={uniqueIDs}
                uniqueLabels={[]}
                idGenerator={genSolutionID}
            />
        </InlineModelAdmin>
        </ProblemsLayout>
    )
}

export default Problem2;